/**
 * The Class overview's one projection of a session plan's readings,
 * materials and coursework. Every source names its records by their own
 * identity, so one record from several sources is one row whose provenance
 * lists each source; a source that could not be read is reported as missing,
 * never as an empty list or a smaller total.
 */
import type { VisitAddress } from "@/components/ui/box.schema";
import type { ActivityCoursework, ActivityReference } from "@/lib/api/courseUnits";
import type { ContentReading } from "@/lib/api/courseContent";
import type { TeachingSessionAssignment } from "@/lib/api/sessionPortal";
import type { TeachingDraftHeader } from "@/lib/api/teachingDrafts";
import type { DraftActivity } from "@/components/courses/unit-planner";
import type { MaterialItem } from "@/components/courses/materials/material-facts";

export const activityNumber = (index: number) => String(index + 1).padStart(2, "0");

export interface OverviewResource {
  key: string;
  title: string;
  kind: "reading" | "material";
  visit: VisitAddress;
  /** Where the plan meets it: "Placed in this session", "In this topic", "Activity 02". */
  from: string[];
}

export interface OverviewWork {
  key: string;
  title: string;
  /** Private writing, or the published record's own status. */
  status: "private" | "draft" | "published" | "closed" | "archived" | "referenced";
  visit: VisitAddress;
  from: string[];
  /** The work's actual placement, when it is not this session's plan. */
  placement: string | null;
}

/** Each source's read; `null` means it could not be read. */
interface Sources {
  courseId: string;
  unitId: string;
  activities: DraftActivity[];
  references: Record<string, ActivityReference> | undefined;
  links: Record<string, ActivityCoursework[]> | undefined;
  library: MaterialItem[] | null;
  topicReadings: ContentReading[] | null;
  sessionWork: TeachingSessionAssignment[] | null;
  drafts: TeachingDraftHeader[] | null;
}

const add = <T extends { key: string; from: string[] }>(rows: Map<string, T>, row: T) => {
  const known = rows.get(row.key);
  if (!known) rows.set(row.key, row);
  else for (const from of row.from) if (!known.from.includes(from)) known.from.push(from);
};

const libraryKey = (item: MaterialItem) => {
  const reading = item.material.reading_id ?? item.placement?.reading_id ?? null;
  return reading ? `reading:${reading}` : `placement:${item.placement?.id ?? item.material.id}`;
};
const libraryVisit = (courseId: string, item: MaterialItem): VisitAddress => {
  const reading = item.material.reading_id ?? item.placement?.reading_id ?? null;
  return reading ? `reading:${courseId}/${reading}` : `material:${courseId}/${item.placement?.id ?? item.material.id}`;
};

/** Readings and materials this class uses, each once. */
export function classResources(sources: Sources): { rows: OverviewResource[]; partial: boolean } {
  const { courseId, unitId, activities, references, library, topicReadings } = sources;
  const rows = new Map<string, OverviewResource>();
  const byPlacement = new Map((library ?? []).filter(item => item.placement).map(item => [item.placement!.id, item]));
  for (const item of library ?? []) {
    if (item.material.unit_id !== unitId || item.role !== "course_content") continue;
    const reading = Boolean(item.material.reading_id ?? item.placement?.reading_id);
    add(rows, { key: libraryKey(item), title: item.material.title, kind: reading ? "reading" : "material", visit: libraryVisit(courseId, item), from: ["Placed in this session"] });
  }
  for (const reading of topicReadings ?? []) add(rows, { key: `reading:${reading.id}`, title: reading.title, kind: "reading", visit: `reading:${courseId}/${reading.id}`, from: ["In this topic"] });
  activities.forEach((activity, index) => {
    const reference = references?.[activity.id];
    if (reference?.status !== "available" || reference.kind === "assignment") return;
    const item = byPlacement.get(reference.id);
    const from = [`Activity ${activityNumber(index)}`];
    if (item) add(rows, { key: libraryKey(item), title: item.material.title, kind: reference.kind, visit: libraryVisit(courseId, item), from });
    else add(rows, { key: `placement:${reference.id}`, title: reference.title, kind: reference.kind, visit: `material:${courseId}/${reference.id}`, from });
  });
  return { rows: [...rows.values()], partial: library === null || topicReadings === null };
}

/** Coursework this class carries or began, each once, with its own state. */
export function classCoursework(sources: Sources): { rows: OverviewWork[]; partial: boolean } {
  const { courseId, unitId, activities, references, links, sessionWork, drafts } = sources;
  const rows = new Map<string, OverviewWork>();
  for (const work of sessionWork ?? []) add(rows, { key: `assignment:${work.id}`, title: work.title, status: work.status, visit: work.status !== "draft" && work.coverage.awaiting_review_count ? `work:${work.id}/review` : `work:${work.id}`, from: ["Placed in this session"], placement: null });
  for (const draft of drafts ?? []) {
    if (draft.state !== "active" || draft.assignment_id !== null || draft.unit_id !== unitId) continue;
    add(rows, { key: `draft:${draft.id}`, title: draft.title || "Untitled coursework", status: "private", visit: `teaching-draft:${courseId}/${draft.id}`, from: ["Placed in this session"], placement: null });
  }
  activities.forEach((activity, index) => {
    const from = [`From activity ${activityNumber(index)}`];
    for (const link of links?.[activity.id] ?? []) {
      if (link.assignment) add(rows, { key: `assignment:${link.assignment.id}`, title: link.assignment.title, status: link.assignment.status, visit: `work:${link.assignment.id}`, from,
        placement: link.assignment.unit_id === unitId ? null : link.assignment.unit_id ? "Placed in another session's plan" : "Not placed in a session" });
      else if (link.draft) add(rows, { key: `draft:${link.draft.id}`, title: link.draft.title || "Untitled coursework", status: "private", visit: `teaching-draft:${courseId}/${link.draft.id}`, from, placement: null });
    }
    // An activity's own coursework reference, saved before links existed, is the same work.
    const reference = references?.[activity.id];
    if (reference?.status === "available" && reference.kind === "assignment")
      add(rows, { key: `assignment:${reference.id}`, title: reference.title, status: "referenced", visit: `work:${reference.id}`, from: [`Activity ${activityNumber(index)}`], placement: null });
  });
  return { rows: [...rows.values()], partial: sessionWork === null || drafts === null };
}

/** One activity's coursework as the teaching view offers it. */
export function activityWork(courseId: string, activityId: string, links: Record<string, ActivityCoursework[]> | undefined, reference: ActivityReference | undefined) {
  const own = links?.[activityId] ?? [];
  const drafts = own.filter(link => link.draft && !link.assignment && link.draft.state === "active");
  const published = own.filter(link => link.assignment);
  const legacy = reference?.status === "available" && reference.kind === "assignment" && !published.some(link => link.assignment!.id === reference.id) ? reference : null;
  return { drafts, published, legacy, create: `activity-work:${courseId}/${activityId}` as VisitAddress };
}
